import { PHYSICS, PHYSICS_FEEL } from '../utils/constants';

export type VehicleInput = {
  throttle: number;
  steer: number;
  handbrake: boolean;
  nitro: boolean;
};

export type VehicleHandlingTuning = {
  accelMultiplier?: number;
  maxSpeedMultiplier?: number;
  nitroSpeedMultiplier?: number;
  nitroAccelMultiplier?: number;
  steeringMultiplier?: number;
  yawResponseMultiplier?: number;
  gripMultiplier?: number;
  driftBoostMultiplier?: number;
};

export type VehicleState = {
  x: number;
  z: number;
  vx: number;
  vz: number;
  /** radians, Three.js Y rotation (+Z forward at 0). */
  heading: number;
  /** 0 = empty, 1 = full — burst consumes tank over NITRO_DURATION. */
  nitroTank: number;
  /** Seconds until nitro can recharge after a burst ends. */
  nitroCooldown: number;
  /** prd2 Day 11: turbo spool before burst. */
  nitroSpool: number;
  /** Active burst time remaining (s). */
  nitroBurstRemaining: number;
  /** Tank snapshot at burst start (drain rate). */
  nitroBurstTankStart: number;
  /** Previous frame nitro key — rising edge starts burst. */
  nitroPrev: boolean;
  /** True last frame after physics — HUD / camera / VFX. */
  nitroBoosting: boolean;
  /** Yaw velocity (rad/s); inertia — steering builds/releases gradually. */
  yawRate: number;
  /** Vertical offset above ground (m); PRD2 Day 5 jumps. */
  airY: number;
  vy: number;
  airborne: boolean;
  /** Heading at jump launch — landing compare for clean vs crash. */
  launchHeading: number;
  /** Slip angle for drift feel (radians). */
  slipAngle: number;
  /** Drift state: 0 = grip, 1 = full drift. */
  driftAmount: number;
  /** Visual body pitch from weight transfer. */
  visualPitch: number;
  /** Visual body roll from weight transfer. */
  visualRoll: number;
  /** Landing wobble timer. */
  landingWobble: number;
  /** Wall scraping state. */
  wallScraping: boolean;
  /** Surface type: 'road' | 'wet' | 'grass'. */
  surfaceType: 'road' | 'wet' | 'grass';
};

export function createVehicleState(
  x: number,
  z: number,
  heading: number,
): VehicleState {
  return {
    x,
    z,
    vx: 0,
    vz: 0,
    heading,
    nitroTank: 0,
    nitroCooldown: 0,
    nitroSpool: 0,
    nitroBurstRemaining: 0,
    nitroBurstTankStart: 0,
    nitroPrev: false,
    nitroBoosting: false,
    yawRate: 0,
    airY: 0,
    vy: 0,
    airborne: false,
    launchHeading: heading,
    slipAngle: 0,
    driftAmount: 0,
    visualPitch: 0,
    visualRoll: 0,
    landingWobble: 0,
    wallScraping: false,
    surfaceType: 'road',
  };
}

/** Reuse an existing state object (same reference) for restart. */
export function resetVehicleState(
  s: VehicleState,
  x: number,
  z: number,
  heading: number,
): void {
  Object.assign(s, createVehicleState(x, z, heading));
}

/** Deep copy for net prediction / server snapshots (plain data only). */
export function cloneVehicleInto(dst: VehicleState, src: VehicleState): void {
  const c = JSON.parse(JSON.stringify(src)) as VehicleState;
  Object.assign(dst, c);
}

function steerRateForSpeed(speedMs: number): number {
  const kmh = speedMs * 3.6;
  const t = Math.min(
    1,
    Math.max(0, kmh / PHYSICS.STEER_SPEED_THRESHOLD_KMH),
  );
  const u = t * t * (3 - 2 * t);
  return (
    PHYSICS.STEER_SPEED_HIGH +
    (PHYSICS.STEER_SPEED_LOW - PHYSICS.STEER_SPEED_HIGH) * (1 - u)
  );
}

/** Steering wheels don’t pivot the body at rest — need some rolling speed. */
function steeringAuthority(speedMs: number): number {
  const { STEER_MIN_SPEED_MS, STEER_RAMP_TO_FULL_MS } = PHYSICS;
  if (speedMs <= STEER_MIN_SPEED_MS) return 0;
  const span = STEER_RAMP_TO_FULL_MS - STEER_MIN_SPEED_MS;
  if (span <= 1e-6) return 1;
  return Math.min(1, (speedMs - STEER_MIN_SPEED_MS) / span);
}

/** Snappier tip-in + less top-end choke so acceleration feels exciting. */
function throttleCurveForward(throttle: number, fwdVel: number): number {
  if (throttle <= 0) return throttle;
  const tip = Math.pow(throttle, 0.68);
  const falloff =
    1 -
    0.28 *
      Math.pow(Math.min(1, Math.max(0, fwdVel / PHYSICS.MAX_SPEED_MS)), 1.52);
  return tip * falloff;
}

function throttleCurveReverse(throttle: number): number {
  if (throttle >= 0) return throttle;
  return -Math.pow(Math.abs(throttle), 0.82);
}

/**
 * Burnout-style: grip kills lateral slip; handbrake + speed = low align = real slide.
 */
export function stepVehiclePhysics(
  s: VehicleState,
  input: VehicleInput,
  dt: number,
  tuning: VehicleHandlingTuning = {},
): void {
  const {
    ACCEL,
    REVERSE_ACCEL_MULT,
    MAX_SPEED_MS,
    NITRO_MAX_SPEED_MS,
    NITRO_EXTRA_ACCEL,
    NITRO_MIN_TANK_TO_USE,
    NITRO_MIN_SPEED_MS,
    NITRO_MIN_FORWARD_MS,
    NITRO_MIN_THROTTLE,
    NITRO_COOLDOWN_S,
    NITRO_DURATION_S,
    NITRO_SPOOL_S,
    NITRO_RECHARGE_MIN_SPEED_MS,
    NITRO_RECHARGE_MIN_THROTTLE,
    NITRO_RECHARGE_PER_S,
    MAX_REVERSE_MS,
    BRAKE_FORCE,
    HANDBRAKE_STEER,
    HANDBRAKE_STEER_FAST,
    HANDBRAKE_FWD_DRAG,
    YAW_RESPONSE,
    YAW_RESPONSE_HANDBRAKE,
    DRIFT_FWD_BONUS_ACCEL,
    GRIP_NORMAL,
    GRIP_DRIFT,
    DRAG_COAST,
    DRAG_BRAKE,
  } = PHYSICS;

  s.nitroCooldown = Math.max(0, s.nitroCooldown - dt);

  const speed = Math.hypot(s.vx, s.vz);
  const sinH = Math.sin(s.heading);
  const cosH = Math.cos(s.heading);
  /** Along body (+ forward, − reverse) before this frame’s yaw — used for reverse steering. */
  const fwdVel0 = s.vx * sinH + s.vz * cosH;

  const backing =
    fwdVel0 < -0.22 ||
    (fwdVel0 <= 0 && input.throttle < -0.06);
  const steerSign = backing ? -1 : 1;

  const hbFast = input.handbrake && speed > 7.5;
  const hbSteerMul = input.handbrake
    ? hbFast
      ? HANDBRAKE_STEER_FAST
      : HANDBRAKE_STEER
    : 1;

  const steerAuth = steeringAuthority(speed);
  const targetYawRate =
    input.steer *
    steerSign *
    steerAuth *
    steerRateForSpeed(speed) *
    hbSteerMul *
    (tuning.steeringMultiplier ?? 1);

  const yawK =
    (input.handbrake ? YAW_RESPONSE_HANDBRAKE : YAW_RESPONSE) *
    (tuning.yawResponseMultiplier ?? 1);
  const yawBlend = 1 - Math.exp(-yawK * dt);
  s.yawRate += (targetYawRate - s.yawRate) * yawBlend;
  s.heading -= s.yawRate * dt;

  const sin = Math.sin(s.heading);
  const cos = Math.cos(s.heading);
  const fX = sin;
  const fZ = cos;
  const rX = cos;
  const rZ = -sin;

  let fwdVel = s.vx * fX + s.vz * fZ;
  let latVel = s.vx * rX + s.vz * rZ;

  const reverseInput = input.throttle < 0;
  const movingBackward = fwdVel0 < -0.06;
  const rollingBackFromStop =
    reverseInput && fwdVel0 <= 0 && Math.abs(fwdVel0) < 0.14;
  const useReverseAccel =
    reverseInput && (movingBackward || rollingBackFromStop);
  const accel =
    ACCEL * (tuning.accelMultiplier ?? 1) * (useReverseAccel ? REVERSE_ACCEL_MULT : 1);

  let throttleEff: number;
  if (input.throttle > 0) {
    throttleEff = throttleCurveForward(input.throttle, fwdVel);
  } else if (input.throttle < 0) {
    throttleEff = throttleCurveReverse(input.throttle);
  } else {
    throttleEff = 0;
  }

  const nitroRising = input.nitro && !s.nitroPrev;
  s.nitroPrev = input.nitro;

  const nitroEligible =
    s.nitroCooldown <= 0 &&
    s.nitroBurstRemaining <= 0 &&
    s.nitroSpool <= 0 &&
    !input.handbrake &&
    input.throttle >= NITRO_MIN_THROTTLE &&
    speed >= NITRO_MIN_SPEED_MS &&
    fwdVel >= NITRO_MIN_FORWARD_MS &&
    s.nitroTank >= NITRO_MIN_TANK_TO_USE;

  if (nitroRising && nitroEligible) {
    s.nitroSpool = NITRO_SPOOL_S;
  }

  if (s.nitroSpool > 0) {
    s.nitroSpool -= dt;
    if (s.nitroSpool <= 0) {
      s.nitroBurstRemaining = NITRO_DURATION_S;
      s.nitroBurstTankStart = Math.max(0.01, s.nitroTank);
    }
  }

  let nitroBoost = false;
  if (s.nitroBurstRemaining > 0) {
    nitroBoost = true;
    s.nitroBurstRemaining -= dt;
    const drainPerS = s.nitroBurstTankStart / NITRO_DURATION_S;
    s.nitroTank = Math.max(0, s.nitroTank - drainPerS * dt);
    if (s.nitroBurstRemaining <= 0 || s.nitroTank <= 0.01) {
      s.nitroBurstRemaining = 0;
      s.nitroTank = 0;
      s.nitroCooldown = NITRO_COOLDOWN_S;
    }
  }

  fwdVel += throttleEff * accel * dt;
  if (nitroBoost) {
    fwdVel += NITRO_EXTRA_ACCEL * (tuning.nitroAccelMultiplier ?? 1) * dt;
  }

  if (input.throttle * fwdVel < 0 && Math.abs(input.throttle) > 0.02) {
    fwdVel *= Math.pow(BRAKE_FORCE, dt * 60);
  }

  /**
   * Lateral damping: strong when gripped; at speed + handbrake, very weak = slide.
   * Slow + handbrake = tighter (parking).
   * TUNED: Lower align values = drift lasts longer
   */
  let align: number;
  if (input.handbrake && speed > 8) {
    const driftDepth = Math.min(1, Math.max(0, (speed - 8) / 24));
    align = 0.22 - driftDepth * 0.09;
  } else if (input.handbrake) {
    align = 1.0 + GRIP_DRIFT * 3.4;
  } else {
    const catchSlip = Math.min(1, Math.abs(latVel) / Math.max(6, speed));
    align = 5.8 + GRIP_NORMAL * (22 + catchSlip * 7);
  }
  align *= tuning.gripMultiplier ?? 1;
  latVel *= Math.exp(-align * dt);

  if (input.handbrake && speed > 8 && fwdVel > 0.4) {
    fwdVel += DRIFT_FWD_BONUS_ACCEL * (tuning.driftBoostMultiplier ?? 1) * dt;
  }

  const maxSpeedCap = MAX_SPEED_MS * (tuning.maxSpeedMultiplier ?? 1);
  const nitroSpeedCap =
    NITRO_MAX_SPEED_MS *
    (tuning.maxSpeedMultiplier ?? 1) *
    (tuning.nitroSpeedMultiplier ?? 1);
  const speedCap = nitroBoost ? nitroSpeedCap : maxSpeedCap;
  fwdVel = Math.max(-MAX_REVERSE_MS, Math.min(speedCap, fwdVel));

  if (input.handbrake && Math.abs(fwdVel) > 1.2 && speed > 6) {
    fwdVel *= Math.pow(HANDBRAKE_FWD_DRAG, dt * 60);
  }

  const drag =
    Math.abs(input.throttle) < 0.02
      ? DRAG_COAST
      : input.throttle * fwdVel < 0
        ? DRAG_BRAKE
        : 1;
  fwdVel *= Math.pow(drag, dt * 60);

  s.vx = fX * fwdVel + rX * latVel;
  s.vz = fZ * fwdVel + rZ * latVel;

  const canRechargeNitro =
    s.nitroCooldown <= 0 &&
    s.nitroBurstRemaining <= 0 &&
    s.nitroSpool <= 0 &&
    !input.nitro &&
    !input.handbrake &&
    input.throttle >= NITRO_RECHARGE_MIN_THROTTLE &&
    speed >= NITRO_RECHARGE_MIN_SPEED_MS &&
    fwdVel > 3;

  if (canRechargeNitro) {
    s.nitroTank = Math.min(1, s.nitroTank + NITRO_RECHARGE_PER_S * dt);
  }

  const mag = Math.hypot(s.vx, s.vz);
  const cap = nitroBoost ? nitroSpeedCap : maxSpeedCap;
  if (mag > cap * 1.02) {
    const k = cap / mag;
    s.vx *= k;
    s.vz *= k;
  }

  s.nitroBoosting = nitroBoost;

  const {
    PITCH_BRAKE_MAX,
    PITCH_ACCEL_MAX,
    ROLL_MAX,
    DRIFT_ENTRY_THRESHOLD,
    SURFACE_ROAD,
    SURFACE_WET,
    SURFACE_GRASS,
  } = PHYSICS_FEEL;

  s.slipAngle = speed > 1 ? Math.atan2(latVel, Math.abs(fwdVel)) : 0;

  const targetDrift = input.handbrake && speed > 8 ? 1 : Math.min(1, Math.abs(s.slipAngle) / DRIFT_ENTRY_THRESHOLD);
  s.driftAmount += (targetDrift - s.driftAmount) * (1 - Math.exp(-8 * dt));

  const accelG = throttleEff * accel / 9.8;
  const brakeG = (input.throttle < 0 && fwdVel > 0) ? Math.abs(input.throttle) * 2 : 0;
  const targetPitch = -brakeG * PITCH_BRAKE_MAX + accelG * PITCH_ACCEL_MAX * 0.5;
  s.visualPitch += (targetPitch - s.visualPitch) * (1 - Math.exp(-12 * dt));

  const lateralG = Math.abs(s.yawRate) * speed / 9.8;
  const rollDir = s.yawRate > 0 ? 1 : -1;
  const targetRoll = rollDir * Math.min(ROLL_MAX, lateralG * 0.15);
  s.visualRoll += (targetRoll - s.visualRoll) * (1 - Math.exp(-10 * dt));

  if (s.landingWobble > 0) {
    s.landingWobble -= dt;
    const wobble = Math.sin(s.landingWobble * 40) * s.landingWobble * 0.1;
    s.visualPitch += wobble;
    s.visualRoll += wobble * 0.5;
  }

  const surfaceGrip = s.surfaceType === 'wet' ? SURFACE_WET :
                      s.surfaceType === 'grass' ? SURFACE_GRASS : SURFACE_ROAD;
  if (surfaceGrip < 1) {
    s.driftAmount = Math.min(1, s.driftAmount + (1 - surfaceGrip) * 0.5);
  }
}
